import React from "react";
import { IStoreState } from "../../../../redux/initialStoreState";
import { StandadCard } from "../../../../components/Cards";
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ICustomerRelatedAccount } from "../../../../redux/customer/customer.types";
import { CSS_CONSTANTS } from "../../../../constants/cssConstants";
import { useSelector } from "react-redux";
import { ERROR_MESSAGES, LoadState } from "../../../../constants/enums";
import { Add, Edit, ErrorOutline } from "@mui/icons-material";
import { CustomerRelDialog } from "./CustomerRelDialog";
import { useDispatchWrapper } from "../../../../hooks";
import { fetchCustomerRelAccountAsync } from "../../../../redux/customer/customerActions";

const INITIAL_STATE: ICustomerRelatedAccount = {
  customer_id: null,
  cross_reference_id: "",
  relative_name: "",
  customer_code: "",
  status: "ACTIVE",
  incremental_number: null,
};

export const CustomerRelAccountsView: React.FC<{ customerId: number }> = ({
  customerId,
}) => {
  const theme = useTheme();

  const { customerRelatedAccount } = useSelector(
    (storeState: IStoreState) => storeState.customer
  );
  const [open, setOpen] = React.useState<ICustomerRelatedAccount | null>(null);
  const dispatch = useDispatchWrapper();

  const handleComplete = () => {
    dispatch(fetchCustomerRelAccountAsync(customerId, false));
    setOpen(null);
  };

  return (
    <>
      <StandadCard
        heading="Related Accounts"
        sx={{ height: "100%" }}
        rightHeading={
          <>
            <Tooltip title="Create">
              <IconButton
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
                onClick={() =>
                  setOpen({ ...INITIAL_STATE, customer_id: customerId })
                }
              >
                <Add
                  sx={{
                    color: "#fff",
                  }}
                />
              </IconButton>
            </Tooltip>
          </>
        }
      >
        <Stack direction={"column"} height={"100%"} spacing={2}>
          {customerRelatedAccount.loading === LoadState.InProgress && (
            <Box display={"flex"} flexWrap={"wrap"} gap={1}>
              <Skeleton variant="rounded" width={140} height={24} />
              <Skeleton variant="rounded" width={140} height={24} />
            </Box>
          )}

          {customerRelatedAccount.loading === LoadState.Loaded && (
            <Box
              display={"flex"}
              flexWrap={"wrap"}
              gap={1}
              alignItems={"flex-start"}
            >
              {customerRelatedAccount.data.map((item) => {
                return (
                  <Row
                    key={item.cross_reference_id}
                    customerId={customerId}
                    data={item}
                  />
                );
              })}
            </Box>
          )}

          {customerRelatedAccount.data.length === 0 && (
            <>
              <Box
                height={"80%"}
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Box display={"flex"} justifyContent={"center"} mb={1}>
                  <ErrorOutline />
                </Box>
                <Box display={"flex"} justifyContent={"center"} width={"80%"}>
                  <Typography
                    variant="body1"
                    textAlign={"center"}
                    fontWeight={600}
                  >
                    {ERROR_MESSAGES.NO_RECORD_FOUND}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </StandadCard>

      {open && (
        <CustomerRelDialog
          open={true}
          data={open}
          onClose={() => setOpen(null)}
          onComplete={handleComplete}
        />
      )}
    </>
  );
};

export const Row: React.FC<{
  data: ICustomerRelatedAccount;
  customerId: number;
}> = ({ data, customerId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // 👈 detect mobile
  const dispatch = useDispatchWrapper();
  const [open, setOpen] = React.useState<ICustomerRelatedAccount | null>(null);
const input = data.relative_name;
const result = input.replace(/\s*\(.*$/, "");

  const handleComplete = () => {
    dispatch(fetchCustomerRelAccountAsync(customerId, false));
    setOpen(null);
  };

  const handleCustomerOverview = () => {
    window.open(
      `/customer-overview/${data.cross_reference_id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <Stack
        direction={"row"}
        onClick={handleCustomerOverview}
        alignItems={"center"}
        spacing={0.75}
        sx={{
          px: 1,
          py: 0.5,
          maxWidth: "100%",
          cursor: "pointer",
          color: "#fff",
          backgroundColor: theme.palette.primary.main,
          borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
          ...(!isMobile && {
            "&:hover .edit-icon": {
              opacity: 1,
              visibility: "visible",
            },
          }),
        }}
      >
        <Tooltip title={`Customer : ${result} (${data.cross_reference_id})`}>
          <Typography
            variant="body2"
            fontSize={"0.75rem"}
            fontWeight={500}
            noWrap
          >
            {`${result} (${data.cross_reference_id})`}
          </Typography>
        </Tooltip>
        <Tooltip title="Edit">
          <Stack
            className="edit-icon"
            sx={{
              p: 0.25,
              backgroundColor: "#fff",
              borderRadius: "100%",
              cursor: "pointer",
              opacity: isMobile ? 1 : 0,
              visibility: isMobile ? "visible" : "hidden",
              transition: "opacity 0.2s ease-in-out",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(data);
            }}
          >
            <Edit
              sx={{ fontSize: "0.85rem", color: theme.palette.grey[700] }}
            />
          </Stack>
        </Tooltip>
      </Stack>

      {open && (
        <CustomerRelDialog
          open={true}
          data={open}
          onClose={() => setOpen(null)}
          onComplete={handleComplete}
        />
      )}
    </>
  );
};
